import { Link, Redirect, Route, BrowserRouter as Router, Switch, useLocation } from 'react-router-dom';
import { confirmNavigation, HistoryEntries } from './component/BuilderLeaveGuard';
import { ThemeProvider } from '@mui/material/styles';
import { Box, GlobalStyles, Typography } from '@mui/material';
import RequireSession from './component/RequireSession';
import TopMenuBar from './component/TopMenuBar';
import Comment from './page/Comment';
import Conversation from './page/Conversation';
import CreateLesson from './page/CreateLesson';
import CustomSentence from './page/CustomSentence';
import DialogPlan from './page/DialogPlan';
import Home from './page/Home';
import Leaderboard from './page/Leaderboard';
import LeaderboardSentenceDetail from './page/LeaderboardSentenceDetail';
import UserSentenceScore from './component/UserSentenceScore';
import Lesson from './page/Lesson';
import Login from './page/Login';
import PilotRequest from './page/PilotRequest';
import OfficeHour from './page/OfficeHour';
import PublicSentenceScore from './page/PublicSentenceScore';
import Registration from './page/Registration';
import Settings from './page/Settings';
import ResetPassword from './page/ResetPassword';
import ResetPasswordEmail from './page/ResetPasswordEmail';
import SentenceScoreReport from './page/SentenceScoreReport';
import TopReferrer from './page/TopReferrer';
import UserDialogPromptReport from './page/UserDialogPromptReport';
import ValidateEmail from './page/ValidateEmail';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AppBarProvider } from './context/AppBarContext';
import { AudioPlayerProvider } from './context/AudioPlayerContext';
import AdminHomePage from './page/AdminHomePage';
import CommentReport from './page/CommentReport';
import CourseOffering from './page/CourseOffering';
import ManualGradeAudio from './page/ManualGradeAudio';
import RegenerateAudio from './page/RegenerateAudio';
import UserAdmin from './page/UserAdmin';
import StudentApp from './mockup/student/StudentApp';
import StudentDesktopExperience from './mockup/studentDesktop/StudentDesktopExperience';
import StudentV2App from './mockup/v2/student/StudentApp';
import StudentDesktopV2Experience from './mockup/v2/studentDesktop/StudentDesktopExperience';
import TeacherExperience from './mockup/teacher/TeacherExperience';
import CreateLessonPdfMockup from './mockup/createLessonPdf/CreateLessonPdfMockup';
import PublicExperience from './mockup/public/PublicExperience';
import VerbaScholaTeacherCoursePage from './page/VerbaScholaTeacherCoursePage';
import VerbaScholaTeacherDashboardPage from './page/VerbaScholaTeacherDashboardPage';
import VerbaScholaTeacherLessonBuilderPage from './page/VerbaScholaTeacherLessonBuilderPage';
import VerbaScholaTeacherPrintablePlanPage from './page/VerbaScholaTeacherPrintablePlanPage';
import { VerbaScholaTeacherHelpPage } from './page/VerbaScholaHelpPage';
import VerbaScholaTeacherAccountPage from './page/VerbaScholaTeacherAccountPage';
import VerbaScholaTeacherLessonPage from './page/VerbaScholaTeacherLessonPage';
import VerbaScholaStudentApp from './page/VerbaScholaStudentApp';
import verbaScholaTheme from './style/verbaScholaTheme';
import { INTER, WARNING_SOFT, WARNING_TEXT, YELLOW } from './style/verbaScholaPalette';

const reducedMotionStyles = {
    '@media (prefers-reduced-motion: reduce)': {
        '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            scrollBehavior: 'auto !important',
            transitionDuration: '0.01ms !important',
        },
    },
};

const redesignedPublicPaths = new Set(['/', '/login', '/pilot', '/registration', '/reset_password_email', '/reset_password', '/validate_email']);

function isPathInSection(pathname: string, section: string) {
    return pathname === section || pathname.startsWith(`${section}/`);
}

const legacyAdminSections = new Set([
    'admin', 'create_lesson', 'dialog_plan', 'course_offering', 'manual_grade_audio',
    'user_admin', 'report', 'regen_audio',
]);
const legacyStudentSections = new Set([
    'lesson', 'sentence', 'chat', 'office_hour', 'leaderboard', 'leaderboard_sentence',
    'user_sentence_score', 'comment', 'settings', 'top_referrer',
]);

function LegacyExperienceBanner({ pathname }: { pathname: string }) {
    const section = pathname.split('/')[1];
    const audience = legacyAdminSections.has(section) ? 'admin' : legacyStudentSections.has(section) ? 'student' : null;
    if (!audience) return null;
    return <Box component="div" role="note" aria-label="Legacy experience" sx={{ flexShrink: 0, px: { xs: 2, sm: 3 }, py: 1,
        bgcolor: WARNING_SOFT, color: WARNING_TEXT, borderBottom: `1px solid ${YELLOW}` }}>
        <Typography sx={{ fontFamily: INTER, fontSize: 13, lineHeight: 1.5 }}>
            <strong>Legacy {audience === 'admin' ? 'admin tools' : 'student experience'}.</strong>{' '}
            {audience === 'admin'
                ? 'These older tools remain available for legacy courses and no longer receive new features. '
                : 'Your older lessons and practice still work here. These pages no longer receive new features. '}
            <Link to={audience === 'admin' ? '/teacher' : '/student'} style={{ color: 'inherit', fontWeight: 700 }}>
                Open the redesigned {audience === 'admin' ? 'teacher workspace' : 'student area'}
            </Link>.
        </Typography>
    </Box>;
}

function AppRoutes() {
    const location = useLocation();
    const isStudentExperience = isPathInSection(location.pathname, '/student');
    const isTeacherExperience = isPathInSection(location.pathname, '/teacher');
    const isMockupStudentExperience = isPathInSection(location.pathname, '/mockup/student');
    const isMockupStudentDesktopExperience = isPathInSection(location.pathname, '/mockup/student_desktop');
    const isMockupTeacherExperience = isPathInSection(location.pathname, '/mockup/teacher');

    if (isPathInSection(location.pathname, '/mockup/v2/student')) {
        return <Route path="/mockup/v2/student" component={StudentV2App} />;
    }

    if (isPathInSection(location.pathname, '/mockup/v2/student_desktop')) {
        return <Route path="/mockup/v2/student_desktop" component={StudentDesktopV2Experience} />;
    }

    if (isStudentExperience) {
        return <ThemeProvider theme={verbaScholaTheme}><GlobalStyles styles={reducedMotionStyles} /><RequireSession section="/student">
            <Route path="/student" component={VerbaScholaStudentApp} />
        </RequireSession></ThemeProvider>;
    }

    // The teacher pages own the whole viewport, like the mockup they replace, so they render
    // outside the legacy TopMenuBar chrome instead of inside the main Switch below.
    if (isTeacherExperience) {
        return (
            <ThemeProvider theme={verbaScholaTheme}><GlobalStyles styles={reducedMotionStyles} /><RequireSession section="/teacher"><Switch>
                <Route exact path="/teacher" component={VerbaScholaTeacherDashboardPage} />
                <Route exact path="/teacher/help" component={VerbaScholaTeacherHelpPage} />
                <Route exact path="/teacher/account" component={VerbaScholaTeacherAccountPage} />
                <Route exact path="/teacher/course/:courseId/lesson/:lessonId/:builderStep(description|plan|exercises)" component={VerbaScholaTeacherLessonBuilderPage} />
                <Route exact path="/teacher/course/:courseId/lesson/:lessonId/print" component={VerbaScholaTeacherPrintablePlanPage} />
                <Route path="/teacher/course/:courseId/lesson/:lessonId/:lessonTab?" component={VerbaScholaTeacherLessonPage} />
                <Route path="/teacher/course/:courseId/:courseTab?" component={VerbaScholaTeacherCoursePage} />
                <Redirect to="/teacher" />
            </Switch></RequireSession></ThemeProvider>
        );
    }

    if (isMockupStudentExperience) {
        return <Route path="/mockup/student" component={StudentApp} />;
    }

    if (isMockupStudentDesktopExperience) {
        return <Route path="/mockup/student_desktop" component={StudentDesktopExperience} />;
    }

    if (location.pathname === '/mockup/create-lesson-pdf') {
        return <ThemeProvider theme={verbaScholaTheme}><GlobalStyles styles={reducedMotionStyles} /><Route exact path="/mockup/create-lesson-pdf" component={CreateLessonPdfMockup} /></ThemeProvider>;
    }

    if (isMockupTeacherExperience) {
        return <Route path="/mockup/teacher" component={TeacherExperience} />;
    }

    // "/login/" and similar would otherwise fall through to the legacy routes and render nothing.
    const withoutTrailingSlash = location.pathname.replace(/\/+$/, '');
    if (withoutTrailingSlash !== location.pathname && redesignedPublicPaths.has(withoutTrailingSlash)) {
        return <Redirect to={{ pathname: withoutTrailingSlash, search: location.search, hash: location.hash, state: location.state }} />;
    }

    // The redesigned public and account pages own the whole viewport, like /student and /teacher.
    if (redesignedPublicPaths.has(location.pathname)) {
        return <ThemeProvider theme={verbaScholaTheme}><GlobalStyles styles={reducedMotionStyles} /><Switch>
            <Route exact path="/" component={Home} />
            <Route exact path="/login" component={Login} />
            <Route exact path="/pilot" component={PilotRequest} />
            <Route exact path="/registration" component={Registration} />
            <Route exact path="/reset_password_email" component={ResetPasswordEmail} />
            <Route exact path="/reset_password" component={ResetPassword} />
            <Route exact path="/validate_email" component={ValidateEmail} />
        </Switch></ThemeProvider>;
    }

    if (isPathInSection(location.pathname, '/mockup/public')) {
        return <Route path="/mockup/public" component={PublicExperience} />;
    }

    return (
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <TopMenuBar />
            <main style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'stretch', overflowY: 'auto' }}>
                <LegacyExperienceBanner pathname={location.pathname} />
                <Switch>
                    <Route exact path="/sentence" component={CustomSentence} />
                    <Route exact path="/chat" component={Conversation} />
                    <Route exact path="/lesson" component={Lesson} />
                    <Route exact path="/lesson/:courseId?/:lessonId?/:dialogId?" component={Lesson} />
                    <Route exact path="/lesson/settings" component={Lesson} />
                    <Route exact path="/settings" component={Settings} />
                    <Route exact path="/top_referrer" component={TopReferrer} />
                    <Route exact path="/comment" component={Comment} />
                    <Route exact path="/office_hour" component={OfficeHour} />
                    <Route exact path="/leaderboard" component={Leaderboard} />
                    <Route path="/leaderboard_sentence/:sentenceId" component={LeaderboardSentenceDetail} />
                    <Route path="/user_sentence_score/:userName" component={UserSentenceScore} />

                    <Route exact path="/admin" component={AdminHomePage} />
                    <Route exact path="/create_lesson" component={CreateLesson} />
                    <Route exact path="/dialog_plan" component={DialogPlan} />
                    <Route exact path="/course_offering" component={CourseOffering} />

                    <Route exact path="/user_admin" component={UserAdmin} />
                    <Route exact path="/report/user_dialog_prompt" component={UserDialogPromptReport} />
                    <Route exact path="/report/sentence_score" component={SentenceScoreReport} />
                    <Route exact path="/report/comment" component={CommentReport} />

                    <Route exact path="/regen_audio" component={RegenerateAudio} />
                    <Route exact path="/manual_grade_audio" component={ManualGradeAudio} />

                    <Route path="/s/:id" component={PublicSentenceScore} />
                </Switch>
            </main>
        </div>
    );
}

export default function App() {


    return (
        <AppBarProvider><AudioPlayerProvider>
            <ToastContainer
                position="bottom-center"
                autoClose={false}
                toastStyle={{ fontFamily: 'Inter, KaiTi, Roboto, Helvetica, Arial, sans-serif' }}
            />
            {/* A builder page's leave dialog answers its Prompt; the navigation (the browser's Back too) waits for it. */}
            <Router getUserConfirmation={confirmNavigation}>
                <HistoryEntries />
                <AppRoutes />
            </Router>
        </AudioPlayerProvider></AppBarProvider>
    );
}
